import { useAuthStore } from '../stores/authStore';
import type { Permission, Scope } from '../api/types/permissions';
import type { RoleAssignmentInfoLocal } from '../api/types/MeLocal';

// DEC-RBAC-091 (Fase 5): removidos RETAILER/STORE/GROUP. Jerarquía: PLATFORM > TENANT > LEVEL_1/2/3.
// Mayor número = scope mayor (cubre los inferiores en scopeCovers, gating de UI).
const SCOPE_ORDER: Record<Scope, number> = {
    PLATFORM: 4,
    TENANT: 3,
    LEVEL_1: 2,
    LEVEL_2: 1,
    LEVEL_3: 0,
};

export function scopeCovers(
    assignment: RoleAssignmentInfoLocal,
    requestedScope: Scope,
    requestedRef?: string,
): boolean {
    if (SCOPE_ORDER[assignment.scope] < SCOPE_ORDER[requestedScope]) return false;
    if (assignment.scopeRef === '*') return true;
    if (assignment.scope === requestedScope) {
        return requestedRef === undefined || assignment.scopeRef === requestedRef;
    }
    // Scope de rank MAYOR (ej. TENANT) cubre los inferiores de su rama (RETAILER/STORE). El front no
    // conoce la pertenencia retailer→tenant; el backend la valida (hasScopeAccess + TerritoryHierarchyService).
    // Para el gating de UI alcanza la jerarquía de rank. Cierra TD-WEBAPP-005.
    return true;
}

export function useAuth() {
    const user = useAuthStore((s) => s.user);
    const permissions = useAuthStore((s) => s.permissions);
    const roleAssignments = useAuthStore((s) => s.roleAssignments);
    // DEC-RBAC-027: niveles BYOID del propio tenant (de `/me`). Las rutas en contexto-tenant derivan
    // `requireExternalId` con `externalScopeLevels.includes('GROUP')` (cierra TD-WEBAPP-022).
    const externalScopeLevels = useAuthStore((s) => s.externalScopeLevels);
    const loading = useAuthStore((s) => s.loading);

    const hasPermission = (p: Permission): boolean => permissions.has(p);
    const hasScopeAccess = (scope: Scope, scopeRef?: string): boolean => {
        if (!user) return false;
        return roleAssignments.some((a) => scopeCovers(a, scope, scopeRef));
    };
    // El usuario opera a nivel Platform (vista multi-tenant) si tiene al menos un assignment
    // de scope PLATFORM. Distingue la "vista superior" del backoffice de la vista tenant/retailer.
    const isPlatform = roleAssignments.some((a) => a.scope === 'PLATFORM');

    return {
        user,
        permissions,
        roleAssignments,
        externalScopeLevels,
        loading,
        hasPermission,
        hasScopeAccess,
        isPlatform,
    };
}
